import React, { useState } from "react";
import { Link } from "react-router-dom";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { useActivePromo, useUserState } from "@moonscape/shared";
import { trackEvent } from "@/analytics";
import { MONETIZATION_EVENTS, SUBSCRIPTION_EVENTS } from "@/analytics/events";
import { Callout, CheckMark, DHead, Pill, Quota } from "@/components/ms";
import { usePipelineState } from "@/hooks/usePipelineState";

const DISMISS_KEY = "upgrade_widget_dismissed_at";
const DISMISS_COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

function isDismissed(): boolean {
  const ts = localStorage.getItem(DISMISS_KEY);
  if (!ts) return false;
  return Date.now() - parseInt(ts, 10) < DISMISS_COOLDOWN_MS;
}

export const UpgradeWidget = () => {
  const { data: userState } = useUserState();
  const { hasActivePromo, promo, timing } = useActivePromo();
  const [dismissed, setDismissed] = useState(isDismissed);
  // A price for a number the user has not seen yet is an interruption, not an
  // offer. `hasFigure` reads operation_status; a transaction count is data
  // shape, not state (docs/design/ui-state-invariants.md I1).
  const { hasFigure } = usePipelineState();

  const metrics = userState?.metrics;
  const isGrandfathered = metrics?.isGrandfathered ?? false;
  const isOverPlanLimit = metrics?.isOverPlanLimit ?? false;
  const isPremium = userState?.isPremium ?? false;
  const planSlug = metrics?.planSlug ?? "free";
  const planTxnLimit = metrics?.planTxnLimit;
  const txCount = metrics?.txCount ?? 0;
  const nextPlanName = metrics?.nextPlanName;
  const nextPlanSlug = metrics?.nextPlanSlug;

  // Hide: grandfathered, dismissed, no next plan, or not over limit (for premium users)
  if (isGrandfathered || dismissed) return null;
  if (isPremium && !isOverPlanLimit) return null;
  if (!isPremium && !hasFigure) return null;
  if (isPremium && !nextPlanSlug) return null;

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setDismissed(true);
  };

  const formatCountdown = (seconds: number) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    if (h > 0) return `${h}h ${m}m`;
    return `${m}m`;
  };

  const dismissButton = (
    <button
      type="button"
      onClick={handleDismiss}
      className="grid h-8 w-8 place-items-center text-ms-dim transition-colors hover:text-ms-ink"
      aria-label="Dismiss"
    >
      <XMarkIcon className="h-4 w-4" />
    </button>
  );

  // Over-limit upsell (premium users who outgrew their tier)
  if (isPremium && isOverPlanLimit && nextPlanName) {
    return (
      <section className="flex flex-col">
        <DHead label="Your plan" right={dismissButton} />
        <Quota
          className="mt-3.5"
          name="You've outgrown your plan"
          tag="Over limit"
          count={txCount.toLocaleString()}
          cap={planTxnLimit ? planTxnLimit.toLocaleString() : "—"}
          fill={1}
          note={
            <>
              {nextPlanName} covers{" "}
              {nextPlanSlug === "concierge" || nextPlanSlug === "launch_assist"
                ? "unlimited"
                : "more"}{" "}
              transactions.
            </>
          }
        />
        <div className="mt-[var(--gap)]">
          <Pill
            as={Link}
            to="/plans"
            onClick={() => {
              trackEvent(SUBSCRIPTION_EVENTS.PAYWALL_SHOWN, {
                trigger: "txn_limit_upsell",
                current_plan: planSlug,
                next_plan: nextPlanSlug,
                txn_count: txCount,
                source: "web",
                had_completed_calc: hasFigure,
              });
            }}
          >
            Upgrade to {nextPlanName}
          </Pill>
        </div>
      </section>
    );
  }

  // Free user CTA
  const displayPrice = hasActivePromo && promo?.discountPercent
    ? `$${Math.round(49 * (1 - promo.discountPercent / 100))}`
    : "$49";

  const premiumFeatures = [
    "Unlimited exchange connections",
    "Full tax engine — all jurisdictions",
    "FIFO, LIFO, HIFO, Share Pooling",
    "Multi-year tax reports",
    "All export formats + TurboTax",
  ];

  return (
    <section className="flex flex-col">
      <DHead label={`Get Moonscape · ${displayPrice}/yr`} right={dismissButton} />

      {isOverPlanLimit && planTxnLimit ? (
        <Quota
          className="mt-3.5"
          name="You're past the free limit"
          tag="Over limit"
          count={txCount.toLocaleString()}
          cap={planTxnLimit.toLocaleString()}
          fill={1}
          note="Upgrade to keep every transaction in your figures."
        />
      ) : (
        <p className="mt-3.5 text-[12.5px] leading-[1.5] text-ms-mute">
          Calculate your crypto taxes in minutes.
        </p>
      )}

      {hasActivePromo && timing && timing.secondsRemaining > 0 && (
        <Callout tone="tan" className="mt-3.5">
          <b>{promo?.name || "Launch Special"}</b> — {formatCountdown(timing.secondsRemaining)}{" "}
          left
          {promo?.discountPercent ? ` (${promo.discountPercent}% off)` : null}
        </Callout>
      )}

      <ul className="mt-3.5 flex flex-col">
        {premiumFeatures.map((feature) => (
          <li
            key={feature}
            className="flex items-center gap-2.5 border-t border-ms-line py-2.5 text-[13px] text-ms-mute first:border-t-0"
          >
            <CheckMark size={11} className="flex-none text-ms-rule" />
            {feature}
          </li>
        ))}
      </ul>

      <div className="mt-[var(--gap)]">
        <Pill
          as={Link}
          to="/plans"
          onClick={() => {
            trackEvent(MONETIZATION_EVENTS.FEATURE_UNLOCK_ATTEMPTED, {
              feature_name: "full_tax_engine",
              feature_type: "premium",
              current_plan: planSlug,
              trigger_reason: isOverPlanLimit ? "txn_limit_exceeded" : "upgrade_widget",
              source: "web",
            });
            trackEvent(SUBSCRIPTION_EVENTS.PAYWALL_SHOWN, {
              trigger: isOverPlanLimit ? "txn_limit_exceeded" : "upgrade_widget",
              source: "web",
              had_completed_calc: hasFigure,
            });
          }}
        >
          Get started
        </Pill>
      </div>
    </section>
  );
};
